import { MainLayout } from "components/Layout"
import { useLang } from "hooks/useLang"
import Skeleton from "components/Skeleton/Skeleton"
import NewEventCard from "components/NewEventCard"
import ListPageLayout from "components/Layout/ListPageLayout"
import { IFinalEvent } from "types/event"
import { sortEventsByStart } from "utils/helpers/events/sortEvents"
import { fetchEvents } from "api"
import { useQuery, QueryClient, dehydrate } from "@tanstack/react-query"
import { queryKeys } from "utils/queryKeys"
import { useRouter } from "next/router"
import { NextSeo } from "next-seo"

export interface AllEventsResponse {
    message: string
    payload: IFinalEvent[]
}

export async function getServerSideProps() {
    const queryClient = new QueryClient()

    try {
        await queryClient.prefetchQuery({
            queryKey: queryKeys.events(),
            queryFn: () => fetchEvents(),
        })
    } catch (error) {
        console.error("Error prefetching events:", error)
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
        },
    }
}

const AllEvents = () => {
    const { t } = useLang()
    const router = useRouter()

    const { data, isLoading, isError, error } = useQuery<AllEventsResponse>({
        queryKey: queryKeys.events(),
        queryFn: () => fetchEvents(),
    })

    const events = data?.payload || []
    const sortedEvents = sortEventsByStart(events)

    if (isError) {
        return <div>Error: {error.message}</div>
    }

    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath =
        (router.asPath || "/").split("#")[0].split("?")[0] || "/"
    const canonicalUrl = `${baseUrl}${currentPath}`

    return (
        <MainLayout>
            <NextSeo
                title={`${t("All_Events")}`}
                canonical={canonicalUrl}
                languageAlternates={(() => {
                    const pathNoLocale = currentPath.replace(
                        /^\/(en-US|ar-SA)(?=\/|$)/,
                        ""
                    )
                    const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
                    const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`
                    return [
                        { hrefLang: "x-default", href: defaultHref },
                        { hrefLang: "ar", href: defaultHref },
                        { hrefLang: "en", href: enHref },
                    ]
                })()}
                openGraph={{ url: canonicalUrl }}
            />
            <ListPageLayout
                isLoading={isLoading}
                title={t("All_Events")}
                desc={""}
            >
                {isLoading ? (
                    <Skeleton
                        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pt-20"
                        type="event_card"
                    />
                ) : sortedEvents.length === 0 ? (
                    <Skeleton
                        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pt-20"
                        type="event_card_empty"
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-5 pt-20 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {sortedEvents.map((item) => (
                            <NewEventCard
                                event={item}
                                key={item?._id}
                                priorityImage={true}
                            />
                        ))}
                    </div>
                )}
            </ListPageLayout>
        </MainLayout>
    )
}

export default AllEvents
